import { useState } from "react";
import axios from "axios";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import config from "../config";
import themeColors from "../theme";

export default function ClinicalUpdateNotifications() {
    const { token } = useAuth();
    const [description, setDescription] = useState("");
    const [preview, setPreview] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const entries = description.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const valid = entries.length > 0 && description.length <= 2000;

    async function sendNotification() {
        if (!valid || sending) return;
        setSending(true);
        setError("");
        setResult(null);
        try {
            const { data } = await axios.post(`${config.SERVER_URL}/api/clinical-updates/notify`,
                { description: description.trim() }, { headers: { Authorization: `Bearer ${token}` } });
            setResult(data);
            setPreview(false);
        } catch (err) {
            setError(err.response?.data?.error || "Could not complete the notification request. You can retry; already processed recipients will be skipped.");
        } finally {
            setSending(false);
        }
    }

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" color={themeColors.teal} gutterBottom>Clinical Updates</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                After publishing new clinical updates, enter the announcement below. This email goes to
                verified users who have not opted out of Clinical Updates emails.
            </Typography>
            <TextField fullWidth label="Recently added" multiline minRows={3} value={description} disabled={sending}
                inputProps={{ maxLength: 2000 }} error={description.length > 2000}
                helperText="Write the updates you want to announce. Each line becomes a bullet (up to 2,000 characters total)."
                onChange={(event) => { setDescription(event.target.value); setResult(null); setError(""); }} />
            <Button variant="contained" sx={{ mt: 2, backgroundColor: themeColors.teal }} disabled={!valid || sending}
                onClick={() => { setError(""); setPreview(true); }}>Preview Clinical Updates email</Button>
            {error && !preview && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {result && <Alert severity={result.failed ? "warning" : "success"} sx={{ mt: 2 }}>
                {result.sent} sent. {result.alreadyProcessed} already processed. {result.skipped} skipped. {result.failed} failed.
                {result.failed > 0 && " Review delivery logs before retrying uncertain deliveries."}
            </Alert>}
            <Dialog open={preview} onClose={() => { if (!sending) setPreview(false); }} fullWidth maxWidth="sm">
                <DialogTitle>Preview Clinical Updates email</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Recipients: verified users subscribed to Clinical Updates emails. Each recipient’s first name will be filled in automatically.
                    </Typography>
                    <Typography fontWeight="bold" mb={2}>Subject: New Clinical Updates available</Typography>
                    <Typography paragraph>Hi [First Name],</Typography>
                    <Typography paragraph><strong>New clinical updates and guidelines have been added to OnCallCME.</strong></Typography>
                    <Typography paragraph>Stay up to date with the latest developments relevant to Emergency Medicine, including new guidelines, recommendations, and important clinical updates.</Typography>
                    <Typography fontWeight="bold">Recently added:</Typography>
                    <ul>{entries.map((entry, index) => <li key={index}>{entry}</li>)}</ul>
                    <Typography color={themeColors.teal} fontWeight="bold" mb={2}>View Clinical Updates →</Typography>
                    <Typography paragraph>We’ll continue adding new updates as important guidelines and recommendations are published.</Typography>
                    <Typography paragraph>— The OnCallCME Team</Typography>
                    <Typography variant="caption" display="block" mt={2}>An unsubscribe and email preferences link is included.</Typography>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button disabled={sending} onClick={() => setPreview(false)}>Back to editing</Button>
                    <Button variant="contained" disabled={sending} onClick={sendNotification}>
                        {sending ? "Sending…" : "Send Clinical Updates email"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
